


















































import Vue, { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { VBTooltip, BFormGroup } from 'bootstrap-vue';
import { debounce } from 'lodash';
import Formula from './components/Formula.vue';
import Elements from './components/Elements.vue';
import Controls from './components/Controls.vue';
import {
  Control,
  ControlType,
  Element,
  Variable,
  Operator,
  Function,
  FormulaBlockType,
  VariableType,
  CalculationBuilderData,
} from './types';
import { createFormula } from './formulaCreator';

export default Vue.extend({
  name: 'RiskCalculationBuilder',
  components: {
    BFormGroup,
    Formula,
    Elements,
    Controls,
  },
  directives: {
    BTooltip: VBTooltip,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object as PropType<CalculationBuilderData>,
      default: () => {},
    },
  },
  data() {
    return {
      blocks: <Array<Variable | Operator | Function>>[],
      selectedBlockIndex: <Number | null>null,
      textFormula: <String>'',
      showValidation: <Boolean>false,
      isFormulaValid: <Boolean>false,
      formulaIsValidating: <Boolean>false,
      formulaErrorMessage: <String>'',
    };
  },
  computed: {
    ...mapGetters(['routes']),
  },
  watch: {
    blocks: {
      handler() {
        const formula = createFormula(this.blocks);
        this.$emit('input', formula);
        this.validateFormula(formula);
      },
      deep: true,
    },
    data: {
      handler() {
        if (this.data.formula) {
          this.textFormula = this.data.formula;
          this.isFormulaValid = true;
          this.showValidation = true;
        }
        if (this.data.formulaWithSlugs) {
          this.$emit('input', this.data.formulaWithSlugs);
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      request: 'request',
    }),
    update(formula: string) {
      this.$emit('input', formula);
    },
    validateFormula(formula: string) {
      this.formulaIsValidating = true;
      this.debounceValidateFormula(formula);
    },
    debounceValidateFormula: debounce(function(formula: string) {
      this.callValidateFormula(formula);
    }, 500),
    async callValidateFormula(formula: string) {
      try {
        await this.request({
          method: 'post',
          url: this.routes.riskFormulaValidate,
          data: { formula },
          defaultErrorHandler: false,
        });
        this.isFormulaValid = true;
        this.formulaErrorMessage = '';
      } catch (error) {
        this.isFormulaValid = false;
        this.formulaErrorMessage = error.response.data.message;
      } finally {
        this.showValidation = true;
        this.formulaIsValidating = false;
      }
    },
    controlClicked(control: Control) {
      let addBlock = {
        type: control.type,
        value: control.value,
      };
      switch (control.type) {
        case ControlType.Remove:
          // remove the text formula or
          // remove the last value from selected or remove the last block
          if (this.blocks.length === 0 && this.textFormula) {
            this.textFormula = '';
            this.isFormulaValid = false;
            this.$emit('input', '');
          } else if (this.selectedBlockIndex !== null) {
            this.removeLastValueFromSelectedFormulaBlock();
          } else {
            this.removeLastFormulaBlock();
          }
          break;
        case ControlType.Operator:
          this.addFormulaBlock(addBlock);
          // unselect the selected formula block
          if (this.selectedBlockIndex !== null) {
            this.unselectFormulaBlock();
          }
          break;
        case ControlType.Function:
          // unselect any formula block then add and select the newly added
          this.unselectFormulaBlock();
          addBlock = { ...addBlock, values: [] };
          this.addFormulaBlock(addBlock);
          this.selectedBlockIndex = this.blocks.length - 1;
          break;
        default:
          break;
      }
    },
    elementClicked(element: Element, variableType: VariableType) {
      // prepare the block and add to selected block or to the end of the formula
      const block = {
        type: FormulaBlockType.Variable,
        variableType,
        value: element.title,
        slug: element.slug,
      };
      if (this.selectedBlockIndex !== null) {
        this.addValueToSelectedFormulaBlock(block);
      } else {
        this.addFormulaBlock(block);
      }
    },
    removeLastFormulaBlock() {
      if (this.blocks.length) {
        // remove selected index if we are removing the selected block
        if (
          this.selectedBlockIndex !== null &&
          this.blocks[this.selectedBlockIndex].type === FormulaBlockType.Function
        ) {
          this.selectedBlockIndex = null;
        }
        this.blocks.pop();
      }
    },
    removeLastValueFromSelectedFormulaBlock() {
      // remove the selected block values or remove the selected block
      const selectedBlock = this.blocks[this.selectedBlockIndex];
      if (selectedBlock.values.length) {
        selectedBlock.values.pop();
      } else {
        this.removeLastFormulaBlock();
      }
    },
    addFormulaBlock(block: Variable | Operator | Function) {
      this.blocks.push(block);
    },
    addValueToSelectedFormulaBlock(block: Variable | Operator | Function) {
      const selectedBlock = this.blocks[this.selectedBlockIndex];
      selectedBlock.values.push(block);
    },
    selectFormulaBlock(blockIndex: number) {
      this.selectedBlockIndex = blockIndex;
    },
    unselectFormulaBlock() {
      this.selectedBlockIndex = null;
    },
  },
});
